import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setNavActiveTool,
  setNavActivePath,
  clearNavSelection,
  clearShortestPath,
  removePointFromPath,
  clearEraserSelectedPoints,
  clearFloorPaths,
} from '../../../../../store/slices/navigationSlice';
import {
  useAutoGenerateSubPaths,
} from '../../../../../components/map/components/Map/Navigation/useNavigationManager'; 
import './connection.css';
import { EraseSvg, PencilSvg, SelectSvg } from '../../../../../components/common/svgIcons';

const PenIcon = () => (
  <PencilSvg fill={'#A8ABAF'}/>
);
const SelectIcon = () => (
  <SelectSvg fill={'#A8ABAF'}/>
);
const HighlighterIcon = () => (
  <EraseSvg fill={'#A8ABAF'}/>
);
 
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);

const RemoveAllIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-route-off-icon lucide-route-off">
    <circle cx="6" cy="19" r="3"/>
    <path d="M9 19h8.5c.4 0 .9-.1 1.3-.2"/>
    <path d="M5.2 5.2A3.5 3.53 0 0 0 6.5 12H12"/>
    <path d="m2 2 20 20"/>
    <path d="M21 15.3a3.5 3.5 0 0 0-3.3-3.3"/>
    <path d="M15 5h-4.3"/>
    <circle cx="18" cy="5" r="3"/>
  </svg>
);

export default function ConnectionToolbar() {
  const dispatch              = useDispatch();
  const activeTool            = useSelector((s) => s.navigation.activeTool);
  const activePath            = useSelector((s) => s.navigation.activePath);
  const shortestPath          = useSelector((s) => s.navigation.shortestPath);
  const eraserSelectedPoints  = useSelector((s) => s.navigation.eraserSelectedPoints);
  const currentFloor          = useSelector((s) => s.api.currentFloor);

  const hasEraserSelection = eraserSelectedPoints.length > 0;
  const isEraserActive     = activeTool === 'highlighter';

  const handleDeleteSelected = () => {
    eraserSelectedPoints.forEach(({ pointId, pathId }) => {
      dispatch(removePointFromPath({ pathId, pointId }));
    });
    dispatch(clearEraserSelectedPoints());
  };

  const handleRemoveAllLines = () => {
    const floorId = currentFloor?.enc_id;
    if (floorId) dispatch(clearFloorPaths(floorId));
  };

  useEffect(() => {
    if (!isEraserActive || !hasEraserSelection) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Delete') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      handleDeleteSelected();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isEraserActive, hasEraserSelection, eraserSelectedPoints]);

  const autoGenerate = useAutoGenerateSubPaths();

  const tools = [
    { id: 'pen',         icon: <PenIcon />,        label: 'Draw path'           },
    { id: 'select',      icon: <SelectIcon />,      label: 'Select / move nodes' },
    { id: 'highlighter', icon: <HighlighterIcon />, label: 'Select path'         },
  ];

  const showPathToggle = activeTool === 'pen';

  useEffect(() => {
    dispatch(setNavActiveTool('pen'));
    return () => {
      dispatch(setNavActiveTool(null));
      dispatch(clearNavSelection());
    };
  }, [dispatch]);

  return (
    <div className="ct-wrapper-body mt-2">
      <div className="ct-wrapper">
        <div className="ct-bar">
          <div className="ct-tools">
            {tools.map((t) => (
              <button
                key={t.id}
                className={`ct-tool-btn ${activeTool === t.id ? 'active' : ''}`}
                onClick={() => {
                  if(shortestPath?.positions){
                    dispatch(clearShortestPath());
                  }
                  dispatch(setNavActiveTool(t.id))
                }}
                title={t.label}
              >
                {t.icon}
              </button>
            ))}
          </div>

          <div className="ct-divider" />

          <button
            className="ct-auto-btn"
            onClick={autoGenerate}
            title="Auto-connect all pins to nearest main path"
          > 
            Auto Generate Connections
          </button>

          {isEraserActive && (
            <>
              <div className="ct-divider" />
              <button
                className="ct-tool-btn"
                title="Remove all lines on this floor"
                style={{ color: '#e03131' }}
                onClick={handleRemoveAllLines}
              >
                <RemoveAllIcon /> 
              </button>
            </>
          )}

          {hasEraserSelection && (
            <>
              <div className="ct-divider" />
              <button
                className="ct-tool-btn"
                title={`Delete ${eraserSelectedPoints.length} selected node(s)`}
                style={{ color: '#e03131' }}
                onClick={handleDeleteSelected}
              >
                <TrashIcon />
              </button>
            </>
          )}

        </div>

        {showPathToggle && (
          <div className="ct-path-toggle">
            <button
              className={`ct-path-btn ${activePath === 'main' ? 'active' : ''}`}
              onClick={() => dispatch(setNavActivePath('main'))}
            >
              Main Path
            </button>
            <button
              className={`ct-path-btn ${activePath === 'sub' ? 'active' : ''}`}
              onClick={() => dispatch(setNavActivePath('sub'))}
            >
              Subpath
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
